import { toast } from '@redwoodjs/web/toast'

type ImpersonationState = {
  isImpersonating?: boolean
  originalUserId?: string
  originalUsername?: string
  originalName?: string
}

export const getImpersonation = (currentUser: unknown): ImpersonationState | undefined => {
  if (!currentUser || typeof currentUser !== 'object') {
    return undefined
  }
  if (!('impersonation' in currentUser)) {
    return undefined
  }
  return (currentUser as { impersonation?: ImpersonationState }).impersonation
}

export const isImpersonatingUser = (currentUser: unknown) =>
  Boolean(getImpersonation(currentUser)?.isImpersonating)


export const IMPERSONATABLE_ROLES = [
  'lead',
  'member',
  'club_supervisor',
] as const

export const canBeImpersonated = (
  roles: Array<string | null> | null | undefined
) => {
  const normalized = (roles || []).filter(Boolean) as string[]
  if (normalized.includes('admin')) {
    return false
  }
  return IMPERSONATABLE_ROLES.some((role) => normalized.includes(role))
}

export const impersonationRoleLabel = (
  roles: Array<string | null> | null | undefined
) => {
  const normalized = (roles || []).filter(Boolean) as string[]
  if (normalized.includes('lead')) {
    return 'Teamlead'
  }
  if (normalized.includes('member')) {
    return 'Member'
  }
  if (normalized.includes('club_supervisor')) {
    return 'ClubSuperUser'
  }
  return normalized[0] || 'användare'
}

type ImpersonateResponse = {
  ok?: boolean
  error?: string
  redirectTo?: string
  userId?: string
}

const impersonateUrl = () =>
  `${process.env.REDWOOD_ENV_SITE_API_URL || '/api'}/impersonate`

const postImpersonation = async (body: Record<string, string>) => {
  const response = await fetch(impersonateUrl(), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  let payload: ImpersonateResponse = {}
  try {
    payload = (await response.json()) as ImpersonateResponse
  } catch {
    payload = {}
  }

  if (!response.ok || payload.error) {
    throw new Error(payload.error || 'Något gick fel')
  }

  return payload
}

export const startImpersonation = async (userId: string) => {
  try {
    const payload = await postImpersonation({ action: 'start', userId })
    window.location.assign(payload.redirectTo || '/')
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Något gick fel')
  }
}

export const stopImpersonation = async (redirectTo?: string) => {
  try {
    const payload = await postImpersonation({ action: 'stop' })
    window.location.assign(redirectTo || payload.redirectTo || '/admin/welcome')
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Något gick fel')
  }
}
